import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import ConsoleStore from '../../stores/ConsoleStore';
import SnackStore from '../../stores/SnackStore';
import PolicyMigrationStore from '../../stores/PolicyMigrationStore';
import { FuseConsoleCardFooter } from '../ConsoleFragments';
import { FTBody } from '../Fuse';
import Card from '../Fuse/Card';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseButton from '../FuseControls/FuseButton';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import FuseCheckbox from '../FuseControls/FuseCheckbox';
import FusePopUp from '../FuseControls/FusePopUp';
import './PolicyMigrationModal.css';

const block = b.lock('PolicyMigrationModal');

class PolicyMigrationModal extends Component {
    static propTypes = {
        policyMigration: PolicyMigrationStore.PropType,
        snack: SnackStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
    };

    static WARNING_COPY = [
        'Activating this Step will impact all users, all activated browsers, and all future Isolation requests.',
        // eslint-disable-next-line max-len
        'Make sure the Policies you are activating are configured to provide the security needed for your entire user base.'
    ]

    saveClick = (activeStep, viewedStep) => {
        if (this.shouldWarn(activeStep, viewedStep)) {
            this.props.policyMigration.confirmIsOpen.setValue(true);
        } else {
            this.completeAction();
        }
    }

    renderHeaderAction = (activeStep, viewedStep) => {
        return <FuseButton
            color="green"
            loading={this.props.policyMigration.saveStatus.isLoading}
            disabled={activeStep === viewedStep}
            qa="policy-migration-save"
            primary
            onClick={() => this.saveClick(activeStep, viewedStep)}
        >Activate Step {viewedStep}</FuseButton>;
    }

    completeAction = () => {
        const { snack, policyMigration, console } = this.props;

        policyMigration.confirmIsOpen.setValue(false);

        policyMigration.save()
            .then(({ useFtrDlpRules, step }) => {
                policyMigration.modalIsOpen.setValue(false);
                policyMigration.activeStep.setValue(step);
                console.setPolicyMigrationStep(policyMigration.stringFromStepNumber(step));
                console.setFileThreatRules(useFtrDlpRules);
                snack.showSuccess(
                    <Fragment>
                        New Policies Migration <b>Step {policyMigration.activeStep.getValue()}</b> is now Active
                    </Fragment>);
            })
            .catch(() => {
                snack.showError(
                    <Fragment>
                        New Policies Migration - failed to activate <b>Step {policyMigration.viewedStep.getValue()}</b>
                    </Fragment>);
            });
    };

    warningMessage = () => {
        return <FuseAlert
            type="warning"
            icon="warning"
            title={'Warning'}
            className={block('Warning')}
            subtitle={<Fragment> - {PolicyMigrationModal.WARNING_COPY[0]} <br />
                {PolicyMigrationModal.WARNING_COPY[1]} </Fragment>}
        />;
    };

    renderStep(step) {
        const commonProps = {
            className: block('Content')
        };

        const footer = <FuseConsoleCardFooter
            text={'Revert back to the Policies [Legacy] structure at any time by ' +
            'viewing Step 1 and clicking "Activate Step 1."'}
        />;

        const step1 = <Card title="Step 1: Review Your Policies [Legacy] Configurations" {...commonProps}>
            <FTBody className={block('Text')}>
                When Step 1 is Activated, Policies [Legacy] are Active across the entire user base.
                New Policies are inactive and hidden on the Navigation bar.
            </FTBody>

            <FTBody className={block('Text')}>
                Review your Policies [Legacy] configurations and the security protections they provide
                for specific users and sites.
            </FTBody>

            <FTBody className={block('Text')}>
                When you are ready to proceed with New Policies Migration,
                <strong> click &quot;View Step 2.&quot;</strong>
            </FTBody>

            {footer}
        </Card>;
        const step2 = <Card title="Step 2: Configure and Test New Policies on Select Users" {...commonProps}>
            <FTBody className={block('Text')}>
                Click <strong> &quot;Activate Step 2.&quot; </strong>
                to update the Navigation bar so it provides New Policies controls.
            </FTBody>

            <FTBody className={block('Text')}>
                Don&apos;t worry, your Policies [Legacy] are still Active for the entire user base.
                New Policies only apply to users that have a defined Browsing Role.
            </FTBody>

            <FuseAlert
                type="success"
                icon="error"
                title={'NOTE'}
                className={block('Warning')}
                subtitle={<Fragment> - Because the Default Browsing Role is <strong>disabled</strong> in this step,
                    the Default Content Rule <strong>ONLY</strong> applies to Browsing Roles that you add. </Fragment>}
            />

            <FTBody className={block('Text')}>
                Familiarize yourself with the New Policies controls, then follow the steps below
                to configure them for test users. If New Policies are working as intended, continue configuring
                new policies for even more users and use cases.
            </FTBody>

            <FTBody className={block('Text')}>
                <div className={block('OptionLabel')}>
                    <strong>Basic Configuration Steps (REQUIRED)</strong>
                </div>
                <span className={block('NL')}>
                    1. Configure the Default Content Rule to either block or allow all Isolated content.
                </span>
                <span className={block('NL')}>
                    2. Add a Browsing Role for test users - <strong>IMPORTANT DO NOT SKIP THIS STEP</strong>
                </span>
                <span className={block('NL')}>
                    3. Add a Redirect Rule for test PPS Cloud User Sync Groups. (URL Isolation only)
                </span>
                <span className={block('NL')}>
                    4. Add an Exclusion Rule for test users (to prevent them
                    from being isolated when visiting specified sites)
                </span>
            </FTBody>

            <FTBody className={block('Text')}>
                If you do not configure all required steps, you will create undesired policy side effects.
            </FTBody>

            <FTBody className={block('Text')}>
                <div className={block('OptionLabel')}>
                    <strong>Advanced Configuration Steps (OPTIONAL)</strong>
                </div>

                <span className={block('NL')}>
                    1. Add Custom Categories to create lists of URLs and IP Addresses.
                </span>
                <span className={block('NL')}>
                    2. Add and prioritize more Browsing Roles to create new user sets with unique options.
                </span>
                <span className={block('NL')}>
                    3. Create and prioritize Rules for the new Browsing Roles and Custom Categories.
                    (For example: add a Content Rule to replicate your acceptable use policies)
                </span>
            </FTBody>

            <FTBody className={block('Text')}>
                When New Policies are ready to be deployed to all users,
                <strong> click &quot;View Step 3.&quot;</strong>
            </FTBody>
            {footer}
        </Card>;
        const step3 = <Card title="Step 3: Edit and Enable the Default Browsing Role">
            <FuseAlert
                type="success"
                icon="error"
                title={'NOTE'}
                className={block('Warning')}
                subtitle={<Fragment> - By activating the Step, you enable the
                    Default Browsing Role which catches all users.
                    This is how New Policies become Active for the entire user base and Policies [Legacy] become
                    Inactive and are no longer recognized by the system. </Fragment>}
            />

            <FTBody className={block('Text')}>
                Before Activating Step 3, <strong>Edit the Default Browsing Role </strong>
                in its disabled state to establish the default options for all users in Isolation
                who do not match other defined Roles.
            </FTBody>

            <FTBody className={block('Text')}>
                <strong>Click &quot;Activate Step 3&quot; </strong> to enable the Default Browsing Role
                and activate New Policies for all users.
            </FTBody>

            <FTBody className={block('Text')}>
                When Policies [Legacy] are no longer needed on the Navigation bar,
                <strong> click &quot;View Step 4.&quot;</strong>
            </FTBody>
            {footer}
        </Card>;
        const step4 = <Card title="Step 4: Hide Policies [Legacy] on the Navigation Bar" {...commonProps}>
            <FTBody className={block('Text')}>
                By activating Step 4, you hide the inactive Policies [Legacy] on the Navigation bar.
            </FTBody>
            <FTBody className={block('Text')}>
                Once Policies [Legacy] are hidden, you have successfully completed the New Policies Migration.
            </FTBody>
            <FTBody className={block('Text')}>
                <strong>Click &quot;Activate Step 4&quot;</strong> to complete the New Policies Migration.
            </FTBody>
            {footer}
        </Card>;

        const stepMap = {
            1: step1,
            2: step2,
            3: step3,
            4: step4
        };
        return stepMap[step];
    }

    renderFooter(activeStep) {
        const previous = activeStep - 1;
        const next = activeStep + 1;
        const goTo = (s) => () => this.props.policyMigration.viewedStep.setValue(s);

        return <div className={block('Footer')}>
            { previous >= 1 ? <FuseButton
                color="blue"
                icon="keyboard_arrow_left"
                qa="policy-migration-prev"
                type="secondary"
                onClick={goTo(previous)}
            > View Step {previous} </FuseButton> : null}
            { next <= 4 ? <FuseButton
                color="blue"
                className={block('BtnNext')}
                iconRight="keyboard_arrow_right"
                qa="policy-migration-next"
                primary
                onClick={goTo(next)}
            >View Step {next}</FuseButton> : null}
        </div>;
    }

    onClose = () =>
        this.props.policyMigration.modalIsOpen.setValue(false);

    shouldWarn = (activeStep, modalStep) => {
        return (activeStep <= 2 && modalStep > 2) ||
            (activeStep > 2 && modalStep <= 2);
    }

    renderStepModal = (modalStep) => {
        const { policyMigration } = this.props;
        const activeStep = policyMigration.activeStep.getValue();
        const viewedStep = policyMigration.viewedStep.getValue();
        const isOpen = policyMigration.modalIsOpen.getValue() && modalStep <= viewedStep;
        const preventMultipleOverlays = modalStep === 1 ? '' : block('DoNotDisplay');

        return <FuseConsoleModal
            key={modalStep}
            isOpen={isOpen}
            title={'Edit New Policies Migration'}
            subTitle={`- [Step ${activeStep} Active]`}
            onClose={this.onClose}
            className={block()}
            headerActions={this.renderHeaderAction(activeStep, modalStep)}
            overlayClassName={preventMultipleOverlays}
        >
            {this.shouldWarn(activeStep, modalStep) ? this.warningMessage() : null}
            {this.renderStep(modalStep)}
            {this.renderFooter(modalStep)}
        </FuseConsoleModal>;
    }

    renderConfirmation() {
        const { policyMigration } = this.props;
        const step = policyMigration.viewedStep.getValue();

        return <FusePopUp
            isOpen={policyMigration.confirmIsOpen.getValue()}
            title={`Confirm Step ${step} Activation`}
            footer={(
                <div className={block('PopupActions')}>
                    <FuseButton
                        type={'secondary'}
                        color={'blue'}
                        className={block('PopupFirstAction')}
                        onClick={() => policyMigration.confirmIsOpen.setValue(false)}
                        qa={'confirm-step-cancel'}
                    >Return to Edit</FuseButton>
                    <FuseButton
                        type={'primary'}
                        color={'blue'}
                        onClick={this.completeAction}
                        disabled={!policyMigration.confirmation.getValue()}
                        qa={'confirm-step-cta'}
                    >
                        Confirm Activation
                    </FuseButton>
                </div>
            )}
        >
            <FTBody>
                <strong>Warning</strong> - {PolicyMigrationModal.WARNING_COPY[0]}
            </FTBody>
            <FTBody className={block('WarningSecondary')}>
                {PolicyMigrationModal.WARNING_COPY[1]}
            </FTBody>
            <FuseCheckbox
                className={block('ConfirmCheckbox')}
                qa="confirm-step-check"
                label={`Confirm the activation of Step ${step}`}
                store={policyMigration.confirmation}
            />
        </FusePopUp>;
    }

    render() {
        return <Fragment>
            {[1, 2, 3, 4].map(this.renderStepModal)}
            {this.renderConfirmation()}
        </Fragment>;
    }
}

export default inject('policyMigration', 'snack', 'console')(observer(PolicyMigrationModal));
